// src/stores/StripeStore.js - Zustand Store following CustomerStore pattern
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useStripeStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (following CustomerStore pattern)
    // ================================
    
    // Stripe connection data
    stripeConnection: null,
    stripeConnectionLoading: false,

    // Setup flow state
    setupInProgress: false,
    onboardingUrl: null,
    
    // Dashboard and management state
    dashboardUrl: null,
    generatingDashboardLink: false,
    
    // Action states
    disconnecting: false,
    refreshingStatus: false,
    
    // Connection state
    isConnected: false,
    
    // Cache state (following pattern)
    cache: {
      stripeConnection: null,
      lastFetch: null
    },
    
    // Internal flags
    _initialized: false,
    
    // ================================
    // SOCKET OPERATIONS (following CustomerStore pattern)
    // ================================
    
    /**
     * Load Stripe connection status - NO RETURN VALUE
     */
    loadStripeConnectionStatus: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ StripeStore: Socket service not initialized');
        return;
      }
      
      console.log('🔌 StripeStore: Loading connection status');
      set({
        stripeConnectionLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_STRIPE_CONNECTION_STATUS, {});
    },
    
    /**
     * Start Express account setup
     * @param {string} email - Business email
     * @param {string} country - Business country (defaults to 'US')
     */
    startExpressSetup: (email, country = 'US') => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ StripeStore: Socket service not initialized');
        return;
      }
      
      if (!email || !email.trim()) {
        console.error('❌ StripeStore: Email is required for setup');
        return;
      }
      
      console.log('🚀 StripeStore: Starting Express setup for:', email);
      set({
        setupInProgress: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.START_EXPRESS_SETUP, {
        email: email.trim(),
        country: country.toUpperCase()
      });
    },
    
    /**
     * Create new onboarding link
     * @param {string} returnUrl - Optional return URL
     * @param {string} refreshUrl - Optional refresh URL
     */
    createOnboardingLink: (returnUrl = null, refreshUrl = null) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ StripeStore: Socket service not initialized');
        return;
      }
      
      console.log('🔗 StripeStore: Creating onboarding link');
      set({
        setupInProgress: true
      });
      
      const data = {};
      if (returnUrl) data.return_url = returnUrl;
      if (refreshUrl) data.refresh_url = refreshUrl;
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_ONBOARDING_LINK, data);
    },
    
    /**
     * Check onboarding status (force refresh)
     */
    checkOnboardingStatus: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ StripeStore: Socket service not initialized');
        return;
      }
      
      console.log('🔄 StripeStore: Checking onboarding status');
      set({
        refreshingStatus: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CHECK_ONBOARDING_STATUS, {});
    },
    
    /**
     * Refresh Stripe capabilities
     */
    refreshStripeCapabilities: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ StripeStore: Socket service not initialized');
        return;
      }
      
      console.log('🔄 StripeStore: Refreshing capabilities');
      set({
        refreshingStatus: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.REFRESH_STRIPE_CAPABILITIES, {});
    },
    
    /**
     * Get Express dashboard link
     */
    getExpressDashboardLink: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ StripeStore: Socket service not initialized');
        return;
      }
      
      console.log('🎛️ StripeStore: Getting dashboard link');
      set({
        generatingDashboardLink: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_EXPRESS_DASHBOARD_LINK, {});
    },
    
    /**
     * Disconnect Stripe account
     */
    disconnectStripeAccount: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ StripeStore: Socket service not initialized');
        return;
      }

      console.log('🔌 StripeStore: Disconnecting account');
      set({
        disconnecting: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.DISCONNECT_STRIPE_ACCOUNT, {});
    },

    /**
     * Clear dashboard URL after it's been opened
     * This prevents the dashboard from auto-opening every time the component remounts
     */
    clearDashboardUrl: () => {
      console.log('🧹 StripeStore: Clearing dashboard URL');
      set({
        dashboardUrl: null
      });
    },
    
    // ================================
    // UTILITY FUNCTIONS (following CustomerStore pattern)
    // ================================
    
    /**
     * Get connection status string
     */
    getConnectionStatus: () => {
      const { stripeConnection } = get();
      return stripeConnection?.connection_status || 'disconnected';
    },
    
    /**
     * Check if payments can be processed
     */
    canProcessPayments: () => {
      const { stripeConnection } = get();
      const status = stripeConnection?.connection_status;
      return ['active', 'charges_only'].includes(status);
    },
    
    /**
     * Check if setup is required
     */
    requiresSetup: () => {
      const { stripeConnection } = get();
      const status = stripeConnection?.connection_status;
      return ['disconnected', 'pending_onboarding', 'restricted'].includes(status);
    },
    
    /**
     * Get user-friendly status message
     */
    getStatusMessage: () => {
      const status = get().getConnectionStatus();
      const messages = {
        disconnected: 'Payments not configured',
        pending_onboarding: 'Payment setup in progress',
        active: 'Payments active',
        restricted: 'Action needed to complete setup',
        charges_only: 'Payments active (payouts pending)'
      };
      return messages[status] || 'Unknown status';
    },
    
    /**
     * Get next action for current status
     */
    getNextAction: () => {
      const status = get().getConnectionStatus();
      const actions = {
        disconnected: 'Set up payments to start accepting credit card payments',
        pending_onboarding: 'Complete your payment setup with Stripe',
        restricted: 'Complete verification requirements to activate payments',
        charges_only: 'Complete bank account setup to receive payouts',
        active: 'Payments are fully active'
      };
      return actions[status] || 'Check payment status';
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setStripeConnectionLoaded: (data) => {
      console.log('📡 StripeStore: Connection status received');
      
      set(state => ({
        stripeConnection: data,
        stripeConnectionLoading: false,
        cache: {
          ...state.cache,
          stripeConnection: data,
          lastFetch: new Date()
        }
      }));
    },
    
    _setStripeAccountCreated: (data) => {
      console.log('📡 StripeStore: Account created');
      
      set(state => ({
        stripeConnection: { ...state.stripeConnection, ...data },
        setupInProgress: false,
        onboardingUrl: data.onboarding_url
      }));
    },
    
    _setOnboardingLinkCreated: (data) => {
      console.log('📡 StripeStore: Onboarding link created');
      
      set({
        onboardingUrl: data.url,
        setupInProgress: false
      });
    },
    
    _setDashboardLinkCreated: (data) => {
      console.log('📡 StripeStore: Dashboard link created');
      
      set({
        dashboardUrl: data.url,
        generatingDashboardLink: false
      });
    },
    
    _setStripeAccountDisconnected: (data) => {
      console.log('📡 StripeStore: Account disconnected');
      
      set(state => ({
        stripeConnection: {
          connection_status: 'disconnected',
          account_type: null,
          charges_enabled: false,
          payouts_enabled: false
        },
        disconnecting: false,
        cache: {
          ...state.cache,
          stripeConnection: null,
          lastFetch: new Date()
        }
      }));
    },
    
    _updateStripeConnection: (data) => {
      console.log('📡 StripeStore: Connection updated');
      
      set(state => ({
        stripeConnection: data,
        refreshingStatus: false,
        cache: {
          ...state.cache,
          stripeConnection: data,
          lastFetch: new Date()
        }
      }));
    },
    
    // ================================
    // SOCKET CONNECTION MANAGEMENT (following CustomerStore pattern)
    // ================================
    
    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },
    
    _getSocketManager: () => {
      // Access socket manager from window global (following pattern)
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },
    
    _handleAuthReset: () => {
      console.log('🧹 StripeStore: Resetting all state due to auth reset');
      
      set({
        // Reset all state to initial values
        stripeConnection: null,
        stripeConnectionLoading: false,
        setupInProgress: false,
        onboardingUrl: null,
        dashboardUrl: null,
        generatingDashboardLink: false,
        disconnecting: false,
        refreshingStatus: false,
        isConnected: false,
        cache: {
          stripeConnection: null,
          lastFetch: null
        },
        _initialized: false
      });
    },
    
    // ================================
    // INITIALIZATION (following CustomerStore pattern)
    // ================================
    
    _initialize: (socketManager) => {
        const state = get();
        
        console.log('🔌 StripeStore: Initializing with socket manager');
        
        // Store socket manager reference
        if (typeof window !== 'undefined') {
            window.__socketManager = socketManager;
        }
        
        set({ 
            _initialized: true,
            isConnected: socketManager?.isAuthenticated || false 
        });
        
        // Auto-load Stripe connection status if authenticated
        if (socketManager?.isAuthenticated) {
            console.log('🔄 StripeStore: Auto-loading Stripe connection status (first time)');
            state.loadStripeConnectionStatus();
        }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER SETUP (following CustomerStore pattern)
// ================================

export const initializeStripeStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 StripeStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for all stripe events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useStripeStore.getState();
    
    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.STRIPE_CONNECTION_STATUS_LOADED:
        store._setStripeConnectionLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.STRIPE_ACCOUNT_CREATED:
        store._setStripeAccountCreated(responseData.data);
        break;
        
      case SOCKET_EVENTS.ONBOARDING_LINK_CREATED:
        store._setOnboardingLinkCreated(responseData.data);
        break;
        
      case SOCKET_EVENTS.EXPRESS_DASHBOARD_LINK_CREATED:
        store._setDashboardLinkCreated(responseData.data);
        break;
        
      case SOCKET_EVENTS.STRIPE_ACCOUNT_DISCONNECTED:
        store._setStripeAccountDisconnected(responseData.data);
        break;
        
      case SOCKET_EVENTS.STRIPE_ONBOARDING_STATUS_UPDATED:
      case SOCKET_EVENTS.STRIPE_ACCOUNT_STATUS_UPDATED:
      case SOCKET_EVENTS.STRIPE_CAPABILITIES_REFRESHED:
        store._updateStripeConnection(responseData.data);
        break;
        
      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_STRIPE_CONNECTION_STATUS_FAILED:
        set({ stripeConnectionLoading: false });
        break;

      case SOCKET_EVENTS.START_EXPRESS_SETUP_FAILED:
      case SOCKET_EVENTS.CREATE_ONBOARDING_LINK_FAILED:
        set({ setupInProgress: false });
        break;

      case SOCKET_EVENTS.CHECK_ONBOARDING_STATUS_FAILED:
      case SOCKET_EVENTS.REFRESH_STRIPE_CAPABILITIES_FAILED:
        set({ refreshingStatus: false });
        break;

      case SOCKET_EVENTS.GET_EXPRESS_DASHBOARD_LINK_FAILED:
        set({ generatingDashboardLink: false });
        break;

      case SOCKET_EVENTS.DISCONNECT_STRIPE_ACCOUNT_FAILED:
        set({ disconnecting: false });
        console.log('❌ StripeStore: Operation failed -', responseData.message);
        break;
        
      default:
        // Not a stripe-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners using existing system
  const stripeEvents = [
    // Success events
    SOCKET_EVENTS.STRIPE_CONNECTION_STATUS_LOADED,
    SOCKET_EVENTS.STRIPE_ACCOUNT_CREATED,
    SOCKET_EVENTS.ONBOARDING_LINK_CREATED,
    SOCKET_EVENTS.STRIPE_ONBOARDING_STATUS_UPDATED,
    SOCKET_EVENTS.STRIPE_ACCOUNT_STATUS_UPDATED,
    SOCKET_EVENTS.STRIPE_CAPABILITIES_REFRESHED,
    SOCKET_EVENTS.EXPRESS_DASHBOARD_LINK_CREATED,
    SOCKET_EVENTS.STRIPE_ACCOUNT_DISCONNECTED,
    // Error events
    SOCKET_EVENTS.GET_STRIPE_CONNECTION_STATUS_FAILED,
    SOCKET_EVENTS.START_EXPRESS_SETUP_FAILED,
    SOCKET_EVENTS.CREATE_ONBOARDING_LINK_FAILED,
    SOCKET_EVENTS.CHECK_ONBOARDING_STATUS_FAILED,
    SOCKET_EVENTS.REFRESH_STRIPE_CAPABILITIES_FAILED,
    SOCKET_EVENTS.GET_EXPRESS_DASHBOARD_LINK_FAILED,
    SOCKET_EVENTS.DISCONNECT_STRIPE_ACCOUNT_FAILED
  ];
  
  // Set up individual event listeners using existing system
  stripeEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useStripeStore.getState()._handleAuthReset();
    });
  }

  
  // Initialize the store
  useStripeStore.getState()._initialize(socketManager);
  
  console.log('✅ StripeStore: Socket event listeners initialized');
};

export default useStripeStore;